import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "资讯", to: "/news" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setSearchOpen(false);
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-page items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-700 text-background-50">
            <i className="ri-film-line text-xl" aria-hidden="true" />
          </span>
          <span className="font-logo text-2xl leading-none tracking-wide text-primary-800">
            茶杯狐影视
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-50 text-primary-800"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center rounded-md border border-background-300 bg-background-100 px-2 transition-all focus-within:border-primary-400 focus-within:bg-background-50 sm:flex ${
              searchOpen ? "w-64" : "w-52"
            }`}
          >
            <i className="ri-search-line text-foreground-400" aria-hidden="true" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视 / 资讯"
              className="w-full bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 sm:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" aria-hidden="true" />
          </button>

          <Link
            to="/search?q="
            className="hidden h-9 items-center gap-1 rounded-md bg-accent-500 px-3 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-600 lg:flex"
          >
            <i className="ri-compass-3-line" aria-hidden="true" />
            发现
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
            aria-label="菜单"
            aria-expanded={menuOpen}
          >
            <i
              className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {searchOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 sm:hidden">
          <form
            onSubmit={handleSearch}
            className="flex items-center rounded-md border border-background-300 bg-background-100 px-2"
          >
            <i className="ri-search-line text-foreground-400" aria-hidden="true" />
            <input
              autoFocus
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视 / 资讯"
              className="w-full bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="cursor-pointer rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-background-50"
            >
              搜索
            </button>
          </form>
        </div>
      ) : null}

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-800"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}